import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import LoginIcon from '@mui/icons-material/Login';
import { useCart, formatPrice } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { ApiClient } from '../../services/api';
import { API_URL } from '../../utils/config';
import { backupCart } from '../../utils/cartPersistence';
import { brandColors } from '../../theme';
import { trackBeginCheckout } from '../../utils/googleTag';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items: cartItems, removeItem, totalPrice } = useCart();
  const { isAuthenticated, user } = useAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Wait a moment for cart to load from storage before redirecting
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 500); // Give cart 500ms to load from storage
    
    return () => clearTimeout(timer);
  }, []);

  // Redirect to store if cart is empty after initial load
  React.useEffect(() => {
    if (initialLoadComplete && cartItems.length === 0 && !loading) {
      navigate('/global-market-reports');
    }
  }, [cartItems.length, navigate, loading, initialLoadComplete]);

  const handleLogin = () => {
    // Backup cart before login to persist across OAuth redirect
    backupCart();
    
    // Navigate to login with return path
    navigate('/login', { state: { from: '/checkout' } });
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    
    // Track begin checkout event for Google Ads
    trackBeginCheckout(
      totalPrice,
      'USD',
      cartItems.map(item => ({
        item_id: item.priceId,
        item_name: item.marketName,
        price: item.price,
        quantity: 1
      }))
    );
    
    try {
      const apiClient = new ApiClient({ authenticatedFetch });
      const response = await apiClient.post(`${API_URL}/api/store/subscriptions/purchase`, {
        items: cartItems.map(item => ({
          priceId: item.priceId,
          marketId: item.marketId,
          marketName: item.marketName
        }))
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Unable to create checkout session. Please try again.');
      }
      
      setLoading(false);
    }
  };

  const handleRemoveItem = (marketId: string) => {
    removeItem(marketId);
  };

  // Group items by interval for better display
  const itemsByInterval = cartItems.reduce((acc, item) => {
    const interval = item.productId.includes('monthly') ? 'Monthly' : 'Yearly';
    if (!acc[interval]) {
      acc[interval] = [];
    }
    acc[interval].push(item);
    return acc;
  }, {} as Record<string, typeof cartItems>);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        component="h1"
        sx={{
          color: brandColors.blue,
          fontWeight: 400,
          fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
          lineHeight: 1.05,
          letterSpacing: '-0.5px',
          mb: 4,
        }}
      >
        Checkout
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Cart Items Section */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Order
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <List>
              {Object.entries(itemsByInterval).map(([interval, items]) => (
                <React.Fragment key={interval}>
                  <ListItem sx={{ bgcolor: 'grey.50', my: 1 }}>
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle2" color="text.secondary">
                          {interval} Subscriptions
                        </Typography>
                      }
                    />
                  </ListItem>
                  {items.map((item, index) => (
                    <React.Fragment key={item.marketId}>
                      <ListItem>
                        <ListItemText
                          primary={item.marketName}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Market Report Access
                              </Typography>
                              <Chip 
                                label={interval}
                                size="small"
                                color="primary"
                                sx={{ mt: 0.5 }}
                              />
                            </>
                          }
                          secondaryTypographyProps={{
                            component: 'div'
                          }}
                        />
                        <Box sx={{ textAlign: 'right', mr: 2 }}>
                          <Typography variant="h6">
                            {formatPrice(item.price)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            per {interval.toLowerCase().replace('ly', '')}
                          </Typography>
                        </Box>
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleRemoveItem(item.marketId)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < items.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Total
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" color="primary">
                  {formatPrice(totalPrice)}
                </Typography>
                {Object.keys(itemsByInterval).length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Mixed billing intervals
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Summary Section */}
        <Box sx={{ width: { xs: '100%', md: 350 } }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              
              {isAuthenticated ? (
                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account
                  </Typography>
                  <Typography variant="body1">
                    {user?.email}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ my: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please sign in to complete your purchase
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Items
                </Typography>
                <Typography variant="body1">
                  {cartItems.length} {cartItems.length === 1 ? 'subscription' : 'subscriptions'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {isAuthenticated ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Billing information will be collected securely by Stripe on the next page.
                  </Alert>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleCheckout}
                    disabled={loading || cartItems.length === 0}
                    startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCartCheckoutIcon />}
                  >
                    {loading ? 'Creating session...' : 'Proceed to Payment'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleLogin}
                  startIcon={<LoginIcon />}
                >
                  Sign in to Checkout
                </Button>
              )}

              <Typography variant="caption" display="block" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                Secure payment powered by Stripe
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};

export default CheckoutPage;